import { apiRequest } from './client';
import type { AnonymousUser } from './types';

const DEVICE_ID_KEY = 'omut_device_id';
const USER_ID_KEY = 'omut_user_id';
const memoryStorage = new Map<string, string>();

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        ready?: () => void;
        expand?: () => void;
        requestFullscreen?: () => void | Promise<void>;
        disableVerticalSwipes?: () => void;
        setHeaderColor?: (color: string) => void;
        setBackgroundColor?: (color: string) => void;
        setBottomBarColor?: (color: string) => void;
        isFullscreen?: boolean;
        isExpanded?: boolean;
        viewportHeight?: number;
        stableViewportHeight?: number;
        isVersionAtLeast?: (version: string) => boolean;
        versionAtLeast?: (version: string) => boolean;
        onEvent?: (eventType: 'viewportChanged' | 'fullscreenChanged' | 'fullscreenFailed' | 'activated', callback: () => void) => void;
      };
    };
  }
}

let telegramWebAppPrepared = false;
let fullscreenRequested = false;

function safelyRunTelegramCall(callback: () => void): void {
  try {
    callback();
  } catch {
    // Telegram clients can throw synchronously for unsupported or invalid WebApp methods.
  }
}

function createDeviceId(): string {
  const cryptoId = globalThis.crypto?.randomUUID?.();
  return cryptoId ?? `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return memoryStorage.get(key) ?? null;
  }
}

function writeStorage(key: string, value: string): void {
  memoryStorage.set(key, value);
  try {
    localStorage.setItem(key, value);
  } catch {
    // In-memory fallback keeps the current session usable if WebView storage is blocked.
  }
}

function removeStorage(key: string): void {
  memoryStorage.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore blocked WebView storage.
  }
}

export function getStoredUserId(): string | null {
  return readStorage(USER_ID_KEY);
}

export function getOrCreateDeviceId(): string {
  const existing = readStorage(DEVICE_ID_KEY);
  if (existing) return existing;

  const deviceId = createDeviceId();
  writeStorage(DEVICE_ID_KEY, deviceId);
  return deviceId;
}

export function getTelegramInitData(): string | null {
  const initData = window.Telegram?.WebApp?.initData;
  return initData && initData.length > 0 ? initData : null;
}

function syncAppViewportHeight(): void {
  const webApp = window.Telegram?.WebApp;
  const height =
    webApp?.viewportHeight ||
    webApp?.stableViewportHeight ||
    Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tg-viewport-height')) ||
    window.visualViewport?.height ||
    window.innerHeight;

  if (height > 0) {
    document.documentElement.style.setProperty('--omut-app-height', `${Math.ceil(height)}px`);
  }
}

function telegramSupports(version: string): boolean {
  const webApp = window.Telegram?.WebApp;
  return Boolean(
    webApp &&
    (
      webApp.isVersionAtLeast?.(version) ??
      webApp.versionAtLeast?.(version) ??
      true
    )
  );
}

function requestTelegramFullscreen(force = false): void {
  const webApp = window.Telegram?.WebApp;
  if (!webApp?.requestFullscreen || webApp.isFullscreen || !telegramSupports('8.0')) return;
  if (fullscreenRequested && !force) return;

  fullscreenRequested = true;
  try {
    void Promise.resolve(webApp.requestFullscreen()).catch(() => {
      fullscreenRequested = false;
    });
  } catch {
    fullscreenRequested = false;
  }
}

export function prepareTelegramWebApp(): void {
  const webApp = window.Telegram?.WebApp;
  syncAppViewportHeight();
  if (!webApp) return;

  safelyRunTelegramCall(() => webApp.ready?.());
  if (telegramSupports('6.1')) {
    safelyRunTelegramCall(() => webApp.setHeaderColor?.('secondary_bg_color'));
    safelyRunTelegramCall(() => webApp.setBackgroundColor?.('#0F1020'));
  }
  if (telegramSupports('7.10')) {
    safelyRunTelegramCall(() => webApp.setBottomBarColor?.('#141526'));
  }
  safelyRunTelegramCall(() => webApp.expand?.());
  if (telegramSupports('7.7')) {
    safelyRunTelegramCall(() => webApp.disableVerticalSwipes?.());
  }
  requestTelegramFullscreen();

  if (telegramWebAppPrepared) return;
  telegramWebAppPrepared = true;

  const resyncTelegramViewport = () => {
    safelyRunTelegramCall(() => webApp.expand?.());
    requestTelegramFullscreen(true);
    syncAppViewportHeight();
  };

  safelyRunTelegramCall(() => webApp.onEvent?.('viewportChanged', resyncTelegramViewport));
  safelyRunTelegramCall(() => webApp.onEvent?.('fullscreenChanged', syncAppViewportHeight));
  safelyRunTelegramCall(() => webApp.onEvent?.('fullscreenFailed', syncAppViewportHeight));
  safelyRunTelegramCall(() => webApp.onEvent?.('activated', resyncTelegramViewport));
  window.visualViewport?.addEventListener('resize', syncAppViewportHeight);
  window.addEventListener('resize', syncAppViewportHeight);
  window.addEventListener('focus', resyncTelegramViewport);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resyncTelegramViewport();
  });
  document.addEventListener('pointerdown', () => requestTelegramFullscreen(true), { once: true, passive: true });

  [80, 250, 700, 1400].forEach((delay) => {
    setTimeout(resyncTelegramViewport, delay);
  });
}

export function getUser(userId: string): Promise<AnonymousUser> {
  return apiRequest<AnonymousUser>(`/users/${userId}`);
}

export async function authorizeTelegramUser(userId?: string | null): Promise<AnonymousUser | null> {
  const initData = getTelegramInitData();
  if (!initData) return null;

  prepareTelegramWebApp();

  const deviceId = getOrCreateDeviceId();
  const user = await apiRequest<AnonymousUser>('/users/telegram/authorize', {
    method: 'POST',
    body: JSON.stringify({
      init_data: initData,
      user_id: userId ?? undefined,
      device_id: deviceId,
    }),
  });
  writeStorage(USER_ID_KEY, user.id);
  return user;
}

export async function createOrGetAnonymousUser(): Promise<AnonymousUser> {
  const storedUserId = getStoredUserId();
  if (getTelegramInitData()) {
    try {
      const telegramUser = await authorizeTelegramUser(storedUserId);
      if (telegramUser) return telegramUser;
    } catch (error) {
      if (!storedUserId) throw error;
      removeStorage(USER_ID_KEY);
      const telegramUser = await authorizeTelegramUser(null);
      if (telegramUser) return telegramUser;
    }
  }

  if (storedUserId) {
    try {
      return await getUser(storedUserId);
    } catch {
      removeStorage(USER_ID_KEY);
    }
  }

  const deviceId = getOrCreateDeviceId();
  const user = await apiRequest<AnonymousUser>('/users/anonymous', {
    method: 'POST',
    body: JSON.stringify({ device_id: deviceId }),
  });
  writeStorage(USER_ID_KEY, user.id);
  return user;
}

export async function updateUserProfile(userId: string, payload: {
  display_name: string;
  email?: string | null;
  phone?: string | null;
  password?: string | null;
}): Promise<AnonymousUser> {
  const user = await apiRequest<AnonymousUser>(`/users/${userId}/profile`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  writeStorage(USER_ID_KEY, user.id);
  return user;
}

export async function loginUser(payload: {
  login: string;
  password: string;
}): Promise<AnonymousUser> {
  const deviceId = getOrCreateDeviceId();
  const user = await apiRequest<AnonymousUser>('/users/login', {
    method: 'POST',
    body: JSON.stringify({ ...payload, device_id: deviceId }),
  });
  const telegramUser = await authorizeTelegramUser(user.id);
  writeStorage(USER_ID_KEY, telegramUser?.id ?? user.id);
  return telegramUser ?? user;
}
